import { getActiveTenantId } from './activeTenant';

const NO_TENANT = '__no_tenant__';

/**
 * Build a `localStorage` key scoped to the active tenant.
 *
 * Per-tenant data kept in the browser — recently-viewed records, favourites,
 * saved filter presets, etc. — must never be stored under a single global key,
 * or it bleeds across tenants in the same browser: open tenant A, view a record,
 * switch to tenant B, and B's UI shows A's data (and clicking it 404s, since the
 * id doesn't exist in B). Scoping the key by tenant id keeps each tenant's data
 * separate and preserved across switches.
 *
 * In React code, pass an explicit `tenantId` (from `useAuthTenantId()`) and add
 * it to the relevant effect deps so the key updates when the tenant changes.
 * Outside React, omit it and the active tenant is resolved from the stores.
 */
export function tenantScopedKey(baseKey: string, tenantId?: string | null): string {
  const resolved = (tenantId ?? getActiveTenantId()) || NO_TENANT;
  return `${baseKey}::t:${resolved}`;
}

/**
 * Remove a now-obsolete UNSCOPED key that predates tenant scoping, so its
 * (possibly cross-tenant) contents stop surfacing. Safe to call repeatedly and
 * during SSR.
 */
export function removeLegacyKey(baseKey: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(baseKey);
  } catch {
    /* ignore storage errors */
  }
}
